import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useCampaign } from '../context/CampaignContext';
import { t } from '../lib/i18n';
import { MainLayout } from '../components/layout/MainLayout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { isGameMaster, getQuestStatusClass, getVisibilityClass, cn } from '../lib/utils';
import { toast } from 'sonner';
import { 
  Plus, 
  Compass,
  ChevronRight,
  Loader2,
  Eye,
  EyeOff,
  Filter
} from 'lucide-react';

const API_URL = '/api';

const QUEST_STATUSES = ['unknown', 'known', 'in_progress', 'completed', 'failed', 'abandoned'];
const QUEST_TYPES = ['main', 'side', 'personal'];

export default function QuestsPage() {
  const { campaignId } = useParams();
  const navigate = useNavigate();
  const { user, language } = useAuth();
  const { currentCampaign } = useCampaign();
  
  const [quests, setQuests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [filterStatus, setFilterStatus] = useState('all');
  
  const [newQuest, setNewQuest] = useState({
    title: '',
    summary: '',
    quest_type: 'side',
    status: 'unknown',
    visibility: 'mj_only'
  });

  const userRole = currentCampaign?.members?.find(m => m.user_id === user?.id)?.role;
  const isMJ = isGameMaster(userRole);

  useEffect(() => {
    fetchQuests();
  }, [campaignId]);

  const fetchQuests = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/campaigns/${campaignId}/quests`);
      setQuests(response.data);
    } catch (error) {
      toast.error(t('error_generic', language));
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!newQuest.title.trim()) {
      toast.error(t('error_required', language));
      return;
    }

    setCreating(true);
    try {
      const response = await axios.post(`${API_URL}/campaigns/${campaignId}/quests`, newQuest);
      toast.success(t('success_saved', language));
      setCreateOpen(false);
      setNewQuest({
        title: '',
        summary: '',
        quest_type: 'side',
        status: 'unknown',
        visibility: 'mj_only'
      });
      navigate(`/campaigns/${campaignId}/quests/${response.data.id}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || t('error_generic', language));
    } finally {
      setCreating(false);
    }
  };

  const filteredQuests = filterStatus === 'all' 
    ? quests 
    : quests.filter(q => q.status === filterStatus);

  // Group quests by status for kanban-like view
  const questsByStatus = QUEST_STATUSES.reduce((acc, status) => {
    acc[status] = filteredQuests.filter(q => q.status === status);
    return acc;
  }, {});

  return (
    <MainLayout>
      <div className="space-y-8 animate-fade-in" data-testid="quests-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="font-cinzel text-3xl font-bold">{t('quests', language)}</h1>
            <p className="text-muted-foreground mt-1">
              {language === 'fr' 
                ? 'Gérez les intrigues et objectifs de votre campagne'
                : 'Manage your campaign plots and objectives'}
            </p>
          </div>
          
          <div className="flex items-center gap-2 flex-wrap">
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger className="w-full sm:w-[180px]" data-testid="filter-status">
                <Filter className="h-4 w-4 mr-2" />
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{language === 'fr' ? 'Tous les statuts' : 'All statuses'}</SelectItem>
                {QUEST_STATUSES.map(status => (
                  <SelectItem key={status} value={status}>
                    {t(`quest_status_${status}`, language)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            {isMJ && (
              <Dialog open={createOpen} onOpenChange={setCreateOpen}>
                <DialogTrigger asChild>
                  <Button className="fantasy-button" data-testid="create-quest-btn">
                    <Plus className="mr-2 h-4 w-4" />
                    {t('quest_create', language)}
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-lg">
                  <form onSubmit={handleCreate}>
                    <DialogHeader>
                      <DialogTitle className="font-cinzel">{t('quest_create', language)}</DialogTitle>
                      <DialogDescription>
                        {language === 'fr' 
                          ? 'Créez une nouvelle quête pour votre campagne'
                          : 'Create a new quest for your campaign'}
                      </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                      <div className="space-y-2">
                        <Label htmlFor="quest-title">{t('quest_title', language)} *</Label>
                        <Input
                          id="quest-title"
                          placeholder={language === 'fr' ? 'Retrouver l\'artefact perdu' : 'Find the lost artifact'}
                          value={newQuest.title}
                          onChange={(e) => setNewQuest(prev => ({ ...prev, title: e.target.value }))}
                          required
                          data-testid="quest-title-input"
                        />
                      </div>
                      
                      <div className="space-y-2">
                        <Label htmlFor="quest-summary">{t('quest_summary', language)}</Label>
                        <Textarea
                          id="quest-summary"
                          placeholder={language === 'fr' 
                            ? 'Description de la quête...'
                            : 'Quest description...'}
                          value={newQuest.summary}
                          onChange={(e) => setNewQuest(prev => ({ ...prev, summary: e.target.value }))}
                          rows={3}
                          data-testid="quest-summary-input"
                        />
                      </div>
                      
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>{t('quest_type', language)}</Label>
                          <Select 
                            value={newQuest.quest_type} 
                            onValueChange={(v) => setNewQuest(prev => ({ ...prev, quest_type: v }))}
                          >
                            <SelectTrigger data-testid="quest-type-select">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {QUEST_TYPES.map(type => (
                                <SelectItem key={type} value={type}>
                                  {t(`quest_type_${type}`, language)}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        
                        <div className="space-y-2">
                          <Label>{t('quest_status', language)}</Label>
                          <Select 
                            value={newQuest.status} 
                            onValueChange={(v) => setNewQuest(prev => ({ ...prev, status: v }))}
                          >
                            <SelectTrigger data-testid="quest-status-select">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {QUEST_STATUSES.map(status => (
                                <SelectItem key={status} value={status}>
                                  {t(`quest_status_${status}`, language)}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                      
                      <div className="space-y-2">
                        <Label>{t('visibility', language)}</Label>
                        <Select 
                          value={newQuest.visibility} 
                          onValueChange={(v) => setNewQuest(prev => ({ ...prev, visibility: v }))}
                        >
                          <SelectTrigger data-testid="quest-visibility-select">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="mj_only">
                              <span className="flex items-center gap-2">
                                <EyeOff className="h-3 w-3" />
                                {t('visibility_mj_only', language)}
                              </span>
                            </SelectItem>
                            <SelectItem value="players">
                              <span className="flex items-center gap-2">
                                <Eye className="h-3 w-3" />
                                {t('visibility_players', language)}
                              </span>
                            </SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <DialogFooter>
                      <Button type="button" variant="outline" onClick={() => setCreateOpen(false)}>
                        {t('cancel', language)}
                      </Button>
                      <Button type="submit" disabled={creating} className="fantasy-button" data-testid="create-quest-submit">
                        {creating && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                        {t('create', language)}
                      </Button>
                    </DialogFooter>
                  </form>
                </DialogContent>
              </Dialog>
            )}
          </div>
        </div>

        {/* Quests Grid */}
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[1, 2, 3].map(i => (
              <Card key={i} className="glass-card">
                <CardContent className="p-6">
                  <div className="animate-pulse space-y-4">
                    <div className="h-6 bg-muted rounded w-3/4" />
                    <div className="h-4 bg-muted rounded w-1/2" />
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : quests.length === 0 ? (
          <Card className="glass-card">
            <CardContent className="py-16 text-center">
              <Compass className="h-16 w-16 mx-auto text-muted-foreground mb-6" />
              <h3 className="font-cinzel text-xl font-bold mb-2">
                {language === 'fr' ? 'Aucune quête' : 'No quests'}
              </h3>
              <p className="text-muted-foreground mb-8 max-w-md mx-auto">
                {language === 'fr'
                  ? 'Créez votre première quête pour donner des objectifs à vos joueurs'
                  : 'Create your first quest to give your players objectives'}
              </p>
              {isMJ && (
                <Button onClick={() => setCreateOpen(true)} className="fantasy-button">
                  <Plus className="mr-2 h-4 w-4" />
                  {t('quest_create', language)}
                </Button>
              )}
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-8">
            {/* Active Quests (Known + In Progress) */}
            {(questsByStatus.known.length > 0 || questsByStatus.in_progress.length > 0) && filterStatus === 'all' && (
              <div className="space-y-4">
                <h2 className="font-cinzel text-lg font-bold">
                  {language === 'fr' ? 'Quêtes actives' : 'Active Quests'}
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {[...questsByStatus.in_progress, ...questsByStatus.known].map((quest) => (
                    <QuestCard key={quest.id} quest={quest} campaignId={campaignId} language={language} isMJ={isMJ} />
                  ))}
                </div>
              </div>
            )}
            
            {/* All quests by status when filtered or remaining */}
            {filterStatus !== 'all' ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {filteredQuests.map((quest) => (
                  <QuestCard key={quest.id} quest={quest} campaignId={campaignId} language={language} isMJ={isMJ} />
                ))}
              </div>
            ) : (
              <>
                {/* Completed */}
                {questsByStatus.completed.length > 0 && (
                  <div className="space-y-4">
                    <h2 className="font-cinzel text-lg font-bold text-success">
                      {t('quest_status_completed', language)}
                    </h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {questsByStatus.completed.map((quest) => (
                        <QuestCard key={quest.id} quest={quest} campaignId={campaignId} language={language} isMJ={isMJ} />
                      ))}
                    </div>
                  </div>
                )}
                
                {/* Unknown (MJ only) */}
                {isMJ && questsByStatus.unknown.length > 0 && (
                  <div className="space-y-4">
                    <h2 className="font-cinzel text-lg font-bold text-muted-foreground">
                      {t('quest_status_unknown', language)} ({language === 'fr' ? 'MJ uniquement' : 'GM only'})
                    </h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {questsByStatus.unknown.map((quest) => (
                        <QuestCard key={quest.id} quest={quest} campaignId={campaignId} language={language} isMJ={isMJ} />
                      ))}
                    </div>
                  </div>
                )}
                
                {/* Failed/Abandoned */}
                {(questsByStatus.failed.length > 0 || questsByStatus.abandoned.length > 0) && (
                  <div className="space-y-4">
                    <h2 className="font-cinzel text-lg font-bold text-muted-foreground">
                      {language === 'fr' ? 'Quêtes terminées' : 'Ended Quests'}
                    </h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {[...questsByStatus.failed, ...questsByStatus.abandoned].map((quest) => (
                        <QuestCard key={quest.id} quest={quest} campaignId={campaignId} language={language} isMJ={isMJ} />
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </div>
    </MainLayout>
  );
}

// Quest Card Component
const QuestCard = ({ quest, campaignId, language, isMJ }) => {
  const navigate = useNavigate();
  
  return (
    <Card 
      className="glass-card card-interactive cursor-pointer"
      onClick={() => navigate(`/campaigns/${campaignId}/quests/${quest.id}`)}
      data-testid={`quest-card-${quest.id}`}
    >
      <CardContent className="p-4">
        <div className="flex items-start justify-between gap-2 mb-2">
          <h3 className="font-medium line-clamp-1">{quest.title}</h3>
          <span className={cn("status-chip flex-shrink-0", getQuestStatusClass(quest.status))}>
            {t(`quest_status_${quest.status}`, language)}
          </span>
        </div>
        
        <div className="flex items-center gap-1.5 mb-3 flex-wrap">
          <span className="text-xs px-2 py-0.5 rounded-full bg-secondary text-secondary-foreground">
            {t(`quest_type_${quest.quest_type}`, language)}
          </span>
          {quest.arc && (
            <span className="text-[10px] px-1.5 py-0.5 rounded-md bg-violet-500/10 text-violet-600 dark:text-violet-400 border border-violet-500/15">
              {quest.arc}
            </span>
          )}
          {quest.act && (
            <span className="text-[10px] px-1.5 py-0.5 rounded-md bg-muted/60 text-muted-foreground">
              {quest.act}
            </span>
          )}
          {isMJ && (
            <span className={cn("visibility-badge", getVisibilityClass(quest.visibility))}>
              {quest.visibility === 'mj_only' ? <EyeOff className="h-3 w-3" /> : <Eye className="h-3 w-3" />}
            </span>
          )}
        </div>
        
        <p className="text-sm text-muted-foreground line-clamp-2">
          {quest.summary || (language === 'fr' ? 'Pas de description' : 'No description')}
        </p>
        
        {quest.objectives?.length > 0 && (
          <div className="mt-3 pt-3 border-t border-border">
            <p className="text-xs text-muted-foreground">
              {quest.objectives.filter(o => o.completed).length}/{quest.objectives.length} {language === 'fr' ? 'objectifs' : 'objectives'}
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

